import { useEffect, useRef, useState } from 'react'

import { getIdentifierExample, getStorefrontPublications, getStorefrontSettings, updateStorefrontSettings } from '@/lib/api/client/storefronts'
import type { IdentifierExample } from '@/lib/api/client/storefronts'
import { PackageProductTypeSchema } from '@/lib/api/schemas/storefronts.schema'
import type { SellerArticlePolicy, StorefrontSettings, StorefrontTarget } from '@/lib/api/schemas/storefronts.schema'
import { warehouseProductTypeLabels } from '@/lib/warehouse/labels'
import { StorefrontModal } from '@/components/storefronts/StorefrontModal'
import { Switch } from '@/components/ui/Switch'

type PackageDefault = StorefrontSettings['ymarketPricingPolicy']['packageDefaults'][number]
type PackageProductType = PackageDefault['productType']
type PackageField = 'lengthCm' | 'widthCm' | 'heightCm' | 'weightKg'
type PackageDraft = Record<PackageProductType, Record<PackageField, string>>

const PACKAGE_FIELDS: readonly { key: PackageField; label: string; step: string }[] = [
  { key: 'lengthCm', label: 'Длина, см', step: '0.1' },
  { key: 'widthCm', label: 'Ширина, см', step: '0.1' },
  { key: 'heightCm', label: 'Высота, см', step: '0.1' },
  { key: 'weightKg', label: 'Вес, кг', step: '0.001' },
]

function packageDraft(defaults: readonly PackageDefault[]): PackageDraft {
  const row = (type: PackageProductType): Record<PackageField, string> => {
    const entry = defaults.find((value) => value.productType === type)
    return {
      lengthCm: entry ? String(entry.lengthCm) : '',
      widthCm: entry ? String(entry.widthCm) : '',
      heightCm: entry ? String(entry.heightCm) : '',
      weightKg: entry ? String(entry.weightKg) : '',
    }
  }
  return {
    ring: row('ring'),
    earrings: row('earrings'),
    pendant: row('pendant'),
    chain: row('chain'),
    bracelet: row('bracelet'),
    necklace: row('necklace'),
    brooch: row('brooch'),
    piercing: row('piercing'),
    wedding_ring: row('wedding_ring'),
    cufflinks: row('cufflinks'),
  }
}

// A row is a parcel only when all four values are positive; an empty row means "not set".
function packageDefaultsFromDraft(draft: PackageDraft): { defaults: PackageDefault[]; incomplete: PackageProductType[] } {
  const defaults: PackageDefault[] = []
  const incomplete: PackageProductType[] = []
  for (const type of PackageProductTypeSchema.options) {
    const row = draft[type]
    const values = PACKAGE_FIELDS.map(({ key }) => row[key].trim())
    if (values.every((value) => value === '')) continue
    const numbers = values.map(Number)
    if (numbers.some((value) => !Number.isFinite(value) || value <= 0)) {
      incomplete.push(type)
      continue
    }
    defaults.push({ productType: type, lengthCm: Number(row.lengthCm), widthCm: Number(row.widthCm), heightCm: Number(row.heightCm), weightKg: Number(row.weightKg) })
  }
  return { defaults, incomplete }
}

const CARD_TARGETS: readonly StorefrontTarget[] = ['wildberries', 'ymarket', 'ozon']
const TARGET_LABELS: Record<StorefrontTarget, string> = {
  wildberries: 'Wildberries',
  ymarket: 'Яндекс Маркет',
  ozon: 'Ozon',
  ori_site: 'ori',
}
const POLICY_OPTIONS: readonly { value: SellerArticlePolicy; label: string }[] = [
  { value: 'article_size', label: 'Артикул + размер' },
  { value: 'uin', label: 'УИН' },
]

function isNewPolicy(value: SellerArticlePolicy): value is 'article_size' | 'uin' {
  return value === 'article_size' || value === 'uin'
}

function policyLabel(value: SellerArticlePolicy): string {
  if (value === 'uin_then_sync_identifier' || value === 'sync_identifier_only') return 'УИН, иначе служебный идентификатор (старая настройка)'
  return value === 'article_size' ? 'Артикул + размер' : 'УИН'
}

// The working contour is the normal case and is not worth a badge; only the
// sandbox changes what a save will do, so only the sandbox announces itself.
function environmentLabel(target: StorefrontTarget, environment: StorefrontSettings['targets'][StorefrontTarget]['environment']): string | null {
  if (target === 'wildberries' && environment === 'sandbox') return 'Песочница WB · реальные товары не затрагиваются'
  return null
}

function copySettings(settings: StorefrontSettings): StorefrontSettings {
  return {
    ...settings,
    ymarketPricingPolicy: { ...settings.ymarketPricingPolicy, packageDefaults: settings.ymarketPricingPolicy.packageDefaults.map((entry) => ({ ...entry })) },
    ymarketPricingPolicyAudit: settings.ymarketPricingPolicyAudit.map((entry) => ({ ...entry, old: { ...entry.old }, new: { ...entry.new } })),
    credentialStatus: { ...settings.credentialStatus },
    targets: {
      ymarket: { ...settings.targets.ymarket, credentialStatus: { ...settings.targets.ymarket.credentialStatus } },
      wildberries: { ...settings.targets.wildberries, credentialStatus: { ...settings.targets.wildberries.credentialStatus } },
      ozon: { ...settings.targets.ozon, credentialStatus: { ...settings.targets.ozon.credentialStatus } },
      ori_site: { ...settings.targets.ori_site, credentialStatus: { ...settings.targets.ori_site.credentialStatus } },
    },
  }
}

export type CardSettingsSection = 'cards' | 'ymarketPricingPolicy'

function changedAtText(value: string | undefined): string {
  if (value === undefined) return '—'
  return new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })
}

export function CardSettingsDialog({
  open,
  sampleProductId,
  onClose,
  focusSection = 'cards',
}: {
  open: boolean
  sampleProductId: string | null
  onClose: () => void
  focusSection?: CardSettingsSection
}) {
  const [settings, setSettings] = useState<StorefrontSettings | null>(null)
  const [draft, setDraft] = useState<StorefrontSettings | null>(null)
  const [examples, setExamples] = useState<Partial<Record<StorefrontTarget, IdentifierExample>>>({})
  const [liveCounts, setLiveCounts] = useState<Partial<Record<StorefrontTarget, number>>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warningTarget, setWarningTarget] = useState<StorefrontTarget | null>(null)
  const [packages, setPackages] = useState<PackageDraft>(() => packageDraft([]))
  const pricingPolicy = useRef<HTMLElement | null>(null)
  const draftLoaded = draft !== null

  useEffect(() => {
    if (!open || !draftLoaded || focusSection !== 'ymarketPricingPolicy') return
    pricingPolicy.current?.scrollIntoView?.({ block: 'start' })
    pricingPolicy.current?.querySelector('input')?.focus()
  }, [draftLoaded, focusSection, open])

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setLoading(true)
    setError(null)
    setWarningTarget(null)
    setLiveCounts({})
    getStorefrontSettings()
      .then((value) => {
        if (!active) return
        setSettings(value)
        setDraft(copySettings(value))
        setPackages(packageDraft(value.ymarketPricingPolicy.packageDefaults))
      })
      .catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить настройки карточек') })
      .finally(() => { if (active) setLoading(false) })
    getStorefrontPublications()
      .then((publications) => {
        if (!active) return
        const counts: Partial<Record<StorefrontTarget, number>> = {}
        for (const target of CARD_TARGETS) counts[target] = publications.filter((publication) => publication.target === target && publication.state === 'live').length
        setLiveCounts(counts)
      })
      .catch(() => { if (active) setLiveCounts({}) })
    return () => { active = false }
  }, [open])

  useEffect(() => {
    if (!open || sampleProductId === null) return undefined
    let active = true
    setExamples({})
    for (const target of CARD_TARGETS) {
      getIdentifierExample(target, sampleProductId)
        .then((value) => { if (active) setExamples((current) => ({ ...current, [target]: value })) })
        .catch((reason: unknown) => {
          if (active) setExamples((current) => ({ ...current, [target]: { identifier: null, source: null, refusal: { code: 'example_unavailable', label: reason instanceof Error ? reason.message : 'Пример недоступен' } } }))
        })
    }
    return () => { active = false }
  }, [open, sampleProductId])

  if (!open) return null

  const updateTarget = (target: StorefrontTarget, update: Partial<StorefrontSettings['targets'][StorefrontTarget]>) => {
    setDraft((current) => current === null ? null : {
      ...current,
      targets: {
        ...current.targets,
        [target]: { ...current.targets[target], ...update },
      },
    })
  }

  const policyChangedTarget = draft !== null && settings !== null
    ? CARD_TARGETS.find((target) => draft.targets[target].sellerArticlePolicy !== settings.targets[target].sellerArticlePolicy && (liveCounts[target] ?? 0) > 0) ?? null
    : null

  const save = async () => {
    if (draft === null) return
    const parcels = packageDefaultsFromDraft(packages)
    if (parcels.incomplete.length > 0) {
      setError(`Упаковка: заполните все четыре значения больше нуля или очистите строку — ${parcels.incomplete.map((type) => warehouseProductTypeLabels[type]).join(', ')}.`)
      return
    }
    if (policyChangedTarget !== null && warningTarget !== policyChangedTarget) {
      setWarningTarget(policyChangedTarget)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const value = await updateStorefrontSettings({ ...draft, ymarketPricingPolicy: { ...draft.ymarketPricingPolicy, packageDefaults: parcels.defaults } })
      setSettings(value)
      setDraft(copySettings(value))
      setPackages(packageDraft(value.ymarketPricingPolicy.packageDefaults))
      setWarningTarget(null)
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки карточек')
    } finally {
      setSaving(false)
    }
  }

  return (
    <StorefrontModal title="Настройки карточек" labelledBy="card-settings-title" onClose={onClose} footer={(
      <>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
        <button type="button" className="btn btn-primary" disabled={saving || loading || draft === null} onClick={() => { void save() }}>{warningTarget === null ? 'Сохранить' : 'Подтвердить сохранение'}</button>
      </>
    )}>
      {loading ? <p role="status" className="text-sm text-ink-muted">Загружаем настройки…</p> : null}
      {error ? <p role="alert" className="mb-3 rounded-md bg-red-50 p-3 text-sm text-st-unpaid">{error}</p> : null}
      {warningTarget !== null ? <p role="alert" className="mb-3 rounded-md border border-gold/50 bg-gold/10 p-3 text-sm text-ink-2">У {liveCounts[warningTarget] ?? 0} карточек на {TARGET_LABELS[warningTarget]} идентификатор изменится — они будут отмечены как требующие перезаливки.</p> : null}
      {draft ? <div className="space-y-3">
        <section ref={pricingPolicy} className="rounded-lg border border-rule p-4" aria-label="Политика цен Яндекс Маркета">
          <h3 className="font-semibold">Цены Яндекс Маркета</h3>
          <p className="mt-1 text-xs text-ink-muted">Все значения действуют для новых рекомендаций. Сохранение не меняет цены на Маркете — только предпросмотр → применить.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {([
              ['floorRubPerGram', 'Пол, ₽/г'],
              ['targetRubPerGram', 'Цель, ₽/г'],
              ['markupFloorPercent', 'Минимальная наценка, %'],
              ['markupTargetPercent', 'Целевая наценка, %'],
              ['discountBaseMarkupPercent', 'Наценка зачёркнутой цены, %'],
            ] as const).map(([key, label]) => (
              <label key={key} className="text-xs font-semibold text-ink-2">
                {label}
                <input
                  className="input mt-1 block w-full font-normal tab-nums"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={draft.ymarketPricingPolicy[key]}
                  onChange={(event) => setDraft((current) => current === null ? null : {
                    ...current,
                    ymarketPricingPolicy: { ...current.ymarketPricingPolicy, [key]: Number(event.target.value) },
                  })}
                />
              </label>
            ))}
          </div>
          <fieldset className="mt-4" aria-label="Упаковка для тарифов Маркета">
            <legend className="text-xs font-semibold text-ink-2">Упаковка для расчёта тарифов</legend>
            <p className="mt-1 text-xs text-ink-muted">В 1С нет габаритов упаковки, поэтому тарифы Маркета считаются по этим значениям. Пустая строка — тариф для этого вида не считается, причина видна в экономике.</p>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-ink-muted">
                    <th className="py-1 pr-2 font-medium">Вид</th>
                    {PACKAGE_FIELDS.map((field) => <th key={field.key} className="py-1 pr-2 font-medium">{field.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PackageProductTypeSchema.options.map((type) => (
                    <tr key={type} data-package-type={type}>
                      <td className="py-1 pr-2 text-ink-2">{warehouseProductTypeLabels[type]}</td>
                      {PACKAGE_FIELDS.map((field) => (
                        <td key={field.key} className="py-1 pr-2">
                          <input
                            aria-label={`${warehouseProductTypeLabels[type]}: ${field.label}`}
                            className="input block w-20 font-normal tab-nums"
                            type="number"
                            min="0"
                            step={field.step}
                            value={packages[type][field.key]}
                            onChange={(event) => {
                              const value = event.target.value
                              setPackages((current) => ({ ...current, [type]: { ...current[type], [field.key]: value } }))
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </fieldset>
          {draft.ymarketPricingPolicyAudit.length > 0 ? (
            <p className="mt-3 text-xs text-ink-muted">Изменено {changedAtText(draft.ymarketPricingPolicyAudit.at(-1)?.changedAt)}</p>
          ) : <p className="mt-3 text-xs text-ink-muted">Ещё не менялось</p>}
        </section>
        {CARD_TARGETS.map((target) => {
          const targetSettings = draft.targets[target]
          const example = examples[target]
          const environment = environmentLabel(target, targetSettings.environment)
          return (
            <section key={target} className="rounded-lg border border-rule p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-semibold">{TARGET_LABELS[target]}</h3>
                {environment ? <span className="text-xs font-semibold text-st-unpaid" data-storefront-environment={targetSettings.environment}>{environment}</span> : null}
              </div>
              <fieldset className="mt-3">
                <legend className="text-xs font-semibold text-ink-2">Идентификатор в карточке</legend>
                <div className="mt-2 flex flex-wrap gap-4">
                  {POLICY_OPTIONS.map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm">
                      <input type="radio" name={`card-policy-${target}`} value={option.value} checked={targetSettings.sellerArticlePolicy === option.value} onChange={() => updateTarget(target, { sellerArticlePolicy: option.value })} />
                      {option.label}
                    </label>
                  ))}
                </div>
                {!isNewPolicy(targetSettings.sellerArticlePolicy) ? <p className="mt-2 text-xs text-ink-muted">Текущая настройка: {policyLabel(targetSettings.sellerArticlePolicy)}. Выберите новый вариант, чтобы изменить её.</p> : null}
              </fieldset>
              <div className="mt-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-ink-2">Можно заливать</p>
                  <p className="text-xs text-ink-muted">{targetSettings.cardPublishingEnabled ? 'Публикация разрешена' : 'Включите, чтобы заливать на эту площадку'}</p>
                </div>
                <Switch checked={targetSettings.cardPublishingEnabled} label={`Можно заливать на ${TARGET_LABELS[target]}`} onChange={() => updateTarget(target, { cardPublishingEnabled: !targetSettings.cardPublishingEnabled })} />
              </div>
              <p className="mt-3 text-xs text-ink-2">Пример: {example?.refusal ? `нет — ${example.refusal.label.toLocaleLowerCase('ru-RU')} — такое изделие не зальём` : example?.identifier ?? 'загружаем…'}</p>
            </section>
          )
        })}
      </div> : null}
    </StorefrontModal>
  )
}
